import { useMemo, useState, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { TableCell, TableRow, Collapse } from '@mui/material';
import JsonViewer from 'shared/JsonViewer';
import * as constants from '@constants';
import CollapseCell from 'components/CollapseCell';
import { setFilters } from 'actionCreators';
import { fetchApiRequestLog, fetchNetworkLog } from 'thunk';
import { falsyCheckDate, makeHeaderCells } from 'utils';
import {
  selectApiNetworkLogByTeam,
  selectNetworkLogByTeam,
  selectSelectedTargetService,
} from 'selectors';
import { FilterType, HeadCell, Item, ApiRequestLogDataType, NetworkLogDataType } from 'types';
import { LinkText } from '../shared/styles';
import useAppNavigation from './useAppNavigation';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

type HookProps = {
  teamId: number;
  logType: string;

  requestId?: string | null;
  sessionId?: string | null;
};

type LogType = 'network-errors' | 'agent-errors';

type DataSelectorReturnType = {
  formattedData: NetworkLogDataType[] | ApiRequestLogDataType[];
  filter: FilterType;
  fetchedSoFar: number;
  total: number;
  isRequesting: boolean;
  error: any;
};

type LogTypeHash = Record<
  LogType,
  {
    getFilterId: () => number;
    getDataSelector: () => any; // Return a selector
    getHeadCells: (formattedData: any[]) => HeadCell[];
    // getFetchNew: (filter: FilterType) => () => void;
    getOnPageChange: (newPage: number, filter: FilterType, fetchedSoFar: number) => () => void;
    getOnRequestSort: (filter: FilterType, property: string) => () => void;
    getOnDateChange: (newStartDate: string, filter: FilterType) => () => void;
  }
>;

const headCellsByLogType = {
  [constants.API_REQUEST_LOG]: makeHeaderCells([
    'created_at',
    'updated_at',
    'id',
    'mosaic_team_id',
    'target_service_id',
    'api_request_id',
  ]),
  [constants.NETWORK_LOG]: makeHeaderCells([
    'createdAt',
    'id',
    'mosaicTeamId',
    'targetServiceId',
    'sessionId',
  ]),
};

const defaultDataType = constants.getDataType(constants.DataType.Project);
const defaultDate = format(toZonedTime(new Date(), 'UTC'), 'yyyy-MM-dd');

const useLogs = ({ requestId, teamId, sessionId, logType }: HookProps) => {
  const dispatch = useAppDispatch();
  const { navigateToIntegrationDetailsPage, navigateToTeamProfile } = useAppNavigation();
  const { name: targetService, id: targetServiceId } = useAppSelector(selectSelectedTargetService);

  const [date, setDate] = useState(defaultDate);

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isAllCollapse, setIsAllCollapse] = useState(false);

  const toggleCollapseAll = useCallback(() => {
    setIsAllCollapse(!isAllCollapse);
  }, [isAllCollapse]);

  const onCollapse = useCallback(
    (id: string) => () => {
      setExpandedRows({
        ...expandedRows,
        [id]: !expandedRows[id],
      });
    },
    [expandedRows],
  );

  useEffect(() => {
    /** For network request log */
    if (sessionId) {
      dispatch(
        fetchNetworkLog({
          mosaicTeamId: teamId,
          sessionId,
          startDate: defaultDate,
        }),
      );
    }

    /** For Api request log */
    if (requestId) {
      dispatch(
        fetchApiRequestLog({
          teamId,
          apiRequestIds: [requestId],
          dataType: defaultDataType.value as string,
        }),
      );
    }
  }, [dispatch, requestId, sessionId, teamId]);

  /** Data type filter action */
  const [dataType, setDataType] = useState<Item>(defaultDataType);

  const onDataTypeChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const selectedDataType = event.target.value as constants.DataType;
      const { getDataType } = constants;
      if (selectedDataType !== dataType.value) {
        dispatch(
          fetchApiRequestLog({
            teamId,
            apiRequestIds: [requestId as string],
            dataType: selectedDataType,
          }),
        );
      }
      setDataType(getDataType(selectedDataType));
    },
    [dataType.value, dispatch, requestId, teamId],
  );

  const onDateChange = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      const selectedDate = event.target.value;
      if (date !== selectedDate) {
        dispatch(
          fetchNetworkLog({
            sessionId,
            mosaicTeamId: teamId,
            startDate: selectedDate,
          }),
        );
      }
      setDate(selectedDate);
    },
    [date, dispatch, sessionId, teamId],
  );

  const logTypeHash: LogTypeHash | any = useMemo(() => {
    return {
      [constants.NETWORK_LOG]: {
        getFilterId: () => constants.filterIds.networkLogByTeam,
        getDataSelector: () => selectNetworkLogByTeam,
        getHeadCells: () => headCellsByLogType[constants.NETWORK_LOG],
        getOnRequestSort: (filter: FilterType, property: string) => () => {
          const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
          dispatch(
            setFilters({
              filterId: filter.id,
              page: 0,
              orderBy: property,
              orderDirection: isAsc ? 'desc' : 'asc',
            }),
          );
        },
        getFiltersToShow: () => ({
          sections: ['datePicker'],
          filtersProps: {
            datePicker: {
              onDayChange: onDateChange,
              defaultDate: date,
            },
          },
        }),
      },
      [constants.API_REQUEST_LOG]: {
        getFilterId: () => constants.filterIds.apiNetworkLog,
        getDataSelector: () => selectApiNetworkLogByTeam,
        getHeadCells: () => headCellsByLogType[constants.API_REQUEST_LOG],
        getOnRequestSort: (filter: FilterType, property: string) => () => {
          const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
          dispatch(
            setFilters({
              filterId: filter.id,
              page: 0,
              orderBy: property,
              orderDirection: isAsc ? 'desc' : 'asc',
            }),
          );
        },
        getFiltersToShow: () => ({
          sections: ['singleValuePicker'],
          filtersProps: {
            singleValuePicker: {
              inputLabel: 'Data Type',
              onValueChange: onDataTypeChange,
              item: dataType,
              items: constants.dataTypes,
            },
          },
        }),
      },
    };
  }, [dataType, date, dispatch, onDataTypeChange, onDateChange]);

  const { getDataSelector, getHeadCells, getOnRequestSort, getFiltersToShow } = useMemo(
    () => logTypeHash[logType],
    [logType, logTypeHash],
  );

  const { formattedData, filter, isRequesting, error }: DataSelectorReturnType =
    useAppSelector(getDataSelector());
  const formattedHeadCells = useMemo(
    () => [
      {
        id: 'collapse',
        label: (
          <CollapseCell
            isExpanded={isAllCollapse}
            useDefaultTextCollapseButton
            onCollapse={toggleCollapseAll}
          />
        ),
      },
      ...getHeadCells(),
    ],
    [isAllCollapse, toggleCollapseAll, getHeadCells],
  );

  const cellRendererHash = useMemo(
    () => ({
      created_at: (data: ApiRequestLogDataType) => falsyCheckDate(data.created_at),
      updated_at: (data: ApiRequestLogDataType) => falsyCheckDate(data.updated_at),
      createdAt: (data: NetworkLogDataType) => falsyCheckDate(data.createdAt),
      updatedAt: (data: NetworkLogDataType) => falsyCheckDate(data.updatedAt) || 'N/A',

      mosaicTeamId: (data: NetworkLogDataType) => {
        return <LinkText onClick={() => navigateToTeamProfile({ teamId })}>{teamId}</LinkText>;
      },
      mosaic_team_id: (data: NetworkLogDataType) => {
        return <LinkText onClick={() => navigateToTeamProfile({ teamId })}>{teamId}</LinkText>;
      },
      targetServiceId: (data: NetworkLogDataType) => {
        return (
          <LinkText
            onClick={navigateToIntegrationDetailsPage(
              teamId,
              targetService,
              targetServiceId as number,
            )}
          >
            {targetServiceId}
          </LinkText>
        );
      },
      target_service_id: (data: ApiRequestLogDataType) => {
        return (
          <LinkText
            onClick={navigateToIntegrationDetailsPage(
              teamId,
              targetService,
              targetServiceId as number,
            )}
          >
            {targetServiceId}
          </LinkText>
        );
      },
      collapse: (data: ApiRequestLogDataType | NetworkLogDataType | any) => {
        const expandRowId = data.createdAt || data.created_at;
        return (
          <CollapseCell
            isExpanded={expandedRows[expandRowId]}
            onCollapse={onCollapse(expandRowId)}
          />
        );
      },
    }),
    [
      expandedRows,
      navigateToIntegrationDetailsPage,
      navigateToTeamProfile,
      onCollapse,
      targetService,
      targetServiceId,
      teamId,
    ],
  );

  const renderTableRows = () => {
    return formattedData.map((data: ApiRequestLogDataType | NetworkLogDataType | any) => {
      const expandRowId = data.createdAt || data.created_at;
      return (
        <>
          <TableRow key={`${expandRowId}-main`}>
            {formattedHeadCells?.map((headCell) => {
              const cellRenderer = cellRendererHash[headCell.id as keyof typeof cellRendererHash];
              return (
                <TableCell key={headCell.id}>
                  {cellRenderer ? cellRenderer(data) : data[headCell.id] || 'N/A'}
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow key={`${expandRowId}-details`}>
            <TableCell sx={{ py: 0 }} colSpan={formattedHeadCells.length}>
              <Collapse in={expandedRows[expandRowId]} timeout="auto" unmountOnExit>
                <JsonViewer name={false} src={data} />
              </Collapse>
            </TableCell>
          </TableRow>
        </>
      );
    });
  };

  const onRequestSort = useCallback(
    (event: any, property: string) => {
      const onRequestSortAction = getOnRequestSort(filter, property);
      onRequestSortAction();
    },
    [filter, getOnRequestSort],
  );

  const filtersToShow = useMemo(() => getFiltersToShow(), [getFiltersToShow]);

  return {
    onRequestSort,
    filtersToShow,
    filter,
    isLoading: isRequesting,
    hasNoData: !isRequesting && formattedData.length === 0,
    data: formattedData,
    headCells: formattedHeadCells,
    renderTableRows,
    isAllCollapse,
  };
};

export default useLogs;
